import { IonPage, IonContent, IonFooter, IonSpinner } from '@ionic/react';
import React, { Suspense, useMemo } from 'react';
import { useRanking } from '../features/ranking/useRanking';
import { useBeaches } from '../features/catalog/useBeaches';
import { useLanguage } from '../shared/i18n/LanguageContext';
import BottomNavBar from '../shared/ui/BottomNavBar';
import HeaderActions from '../shared/ui/HeaderActions';
import BrandLogo from '../shared/ui/BrandLogo';
import SeoHead from '../shared/seo/SeoHead';
import './MapPage.css';

// Leaflet is the heaviest dependency in the app and only this route uses
// it, so the whole canvas is code-split INSIDE the page — the only split
// IonRouterOutlet tolerates (see App.tsx). Data is fetched here so the
// requests start while the chunk downloads.
const MapCanvas = React.lazy(() => import('./map/MapCanvas'));

const MapPage: React.FC = () => {
  const catalog = useBeaches();
  const { t } = useLanguage();

  // The markers read the ranking in force, so they repaint with every screen
  // that paints a sky — including when the answer the service worker gave up
  // on lands late.
  const { ranking } = useRanking();
  const weatherMap = useMemo(
    () => new Map((ranking?.resumenTodas ?? []).map((b) => [b.codigo, b])),
    [ranking],
  );

  // Only beaches with real coordinates get a marker, west to east.
  const beaches = useMemo(
    () =>
      catalog.status === 'ready'
        ? catalog.beaches
            .filter(
              (p) =>
                typeof p.lat === 'number' &&
                typeof p.lon === 'number' &&
                p.lat !== 0 &&
                p.lon !== 0
            )
            .sort((a, b) => a.lon - b.lon)
        : [],
    [catalog],
  );

  return (
    <IonPage className="map-page">
      <SeoHead
        title={t('seo.tituloMapa')}
        description={t('seo.descMapa')}
        canonicalPath="/mapa"
      />
      {/* Recargar al tocar el encabezado, pero SOLO sobre el título: cuando el
          manejador estaba en el contenedor, el clic en la ⓘ y en el selector
          de idioma burbujeaba hasta aquí y recargaba la página en vez de
          abrir el menú. `.header-actions` va en absoluto, así que envolver el
          texto no mueve nada. */}
      <div className="map-sticky-header">
        <div
          className="map-sticky-brand brand-with-logo"
          onClick={() => window.location.reload()}
          style={{ cursor: 'pointer' }}
        >
          <BrandLogo />
          <div className="brand-text">
            <h1 className="map-sticky-title">{t('app.titulo')}</h1>
            <p className="map-sticky-subtitle">{t('mapa.subtitulo')}</p>
          </div>
        </div>
        <HeaderActions />
      </div>

      <IonContent className="map-content">
        <Suspense
          fallback={
            <div className="map-loading">
              <IonSpinner name="crescent" />
            </div>
          }
        >
          <MapCanvas beaches={beaches} weatherMap={weatherMap} />
        </Suspense>
      </IonContent>
      <IonFooter className="ion-no-border"><BottomNavBar /></IonFooter>
    </IonPage>
  );
};

export default MapPage;
